import {
  founderDetailSchema,
  founderListSchema,
  type ChangeFounderStatusRequest,
  type CreateFounderProfileRequest,
  type UpdateFounderProfileRequest,
} from '@mfos/shared';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '../../lib/api-client';
import { workspaceKey, workspacePath } from '../workspace/workspace.api';
import { useWorkspaceMutation } from '../workspace/workspace-mutation';

export const foundersKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'founders'] as const;
export const founderKey = (workspaceId: string, founderId: string) =>
  [...foundersKey(workspaceId), founderId] as const;

export function useFounders(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: foundersKey(workspaceId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'founders'), { schema: founderListSchema, signal }),
    enabled,
  });
}

export function useFounder(workspaceId: string, founderId: string, enabled = true) {
  return useQuery({
    queryKey: founderKey(workspaceId, founderId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'founders', founderId), {
        schema: founderDetailSchema,
        signal,
      }),
    enabled: enabled && founderId !== '',
  });
}

export function useCreateFounder(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    (body: CreateFounderProfileRequest) =>
      apiPost(workspacePath(workspaceId, 'founders'), body, founderDetailSchema),
    { refreshMemberships: true },
  );
}

export function useUpdateFounder(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ founderId, body }: { founderId: string; body: UpdateFounderProfileRequest }) =>
      apiPatch(workspacePath(workspaceId, 'founders', founderId), body, founderDetailSchema),
  );
}

export function useChangeFounderStatus(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ founderId, body }: { founderId: string; body: ChangeFounderStatusRequest }) =>
      apiPost(
        workspacePath(workspaceId, 'founders', founderId, 'status'),
        body,
        founderDetailSchema,
      ),
  );
}
